/**
 * A aba "Documentos" do card da oportunidade: PDF, fotos, vídeos e outros
 * arquivos, guardados como anexo da oportunidade no CRM (24/09/2026).
 *
 * ── A interface, refeita em 25/09/2026 ───────────────────────────────────
 *
 * Pedido: a aba estava pobre — o seletor de arquivo nativo do navegador, um
 * botão "Enviar" cinza sempre à vista, e os anexos em linhas de texto, sem
 * prévia. Agora:
 *
 *   · uma área de soltar arquivos, que também abre o seletor ao clicar;
 *   · a FILA antes do envio, com miniatura e como tirar cada um;
 *   · os anexos em grade, com a própria foto, o primeiro quadro do vídeo, ou
 *     o ícone do tipo;
 *   · foto e vídeo abrem no visualizador; PDF e o resto, em aba nova.
 *
 * As chamadas à API são as mesmas de antes.
 *
 * ── O link de abrir vence em cinco minutos ───────────────────────────────
 *
 * Cada documento vem com um link assinado que vale cinco minutos, e agora ele
 * também é a fonte da prévia. A lista é relida a cada quatro minutos enquanto
 * a aba está aberta, para a prévia e o "Abrir" nunca usarem um link vencido.
 */

import { useCallback, useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LIMITES_DO_DOCUMENTO, type DocumentoDaOportunidade } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Dialogo, Icone } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  VisualizadorDeMidia,
  type MidiaParaVer,
} from '@/shared/ui/visualizador-de-midia/visualizador-de-midia';
import '@/shared/ui/documentos/documentos.css';

/** Quatro minutos: antes de o link de cinco vencer. */
const RELER_A_CADA_MS = 4 * 60 * 1000;
const LIMITE_EM_BYTES = LIMITES_DO_DOCUMENTO.megabytesPorArquivo * 1024 * 1024;

/** O tipo que a tela mostra, com a cor da etiqueta. */
type TipoNaTela = 'foto' | 'pdf' | 'video' | 'arquivo';

function tipoDoDocumento(doc: DocumentoDaOportunidade): TipoNaTela {
  if (doc.categoria === 'imagem') return 'foto';
  if (doc.categoria === 'video') return 'video';
  if (doc.extensao?.toLowerCase() === '.pdf') return 'pdf';
  return 'arquivo';
}

/** Na fila, antes de subir: pelo tipo do arquivo, e pela extensão se faltar. */
function tipoDoArquivo(arquivo: File): TipoNaTela {
  const nome = arquivo.name.toLowerCase();
  if (arquivo.type.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic)$/.test(nome)) {
    return 'foto';
  }
  if (arquivo.type.startsWith('video/') || /\.(mp4|mov|webm|m4v)$/.test(nome)) return 'video';
  if (arquivo.type === 'application/pdf' || nome.endsWith('.pdf')) return 'pdf';
  return 'arquivo';
}

const ETIQUETA: Readonly<Record<TipoNaTela, string>> = {
  foto: 'foto',
  pdf: 'PDF',
  video: 'vídeo',
  arquivo: 'arquivo',
};

function quando(instante: string): string {
  return new Date(instante).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function arquivosNoPlural(n: number): string {
  return n === 1 ? '1 arquivo' : `${n} arquivos`;
}

interface NaFila {
  readonly chave: string;
  readonly arquivo: File;
  /** A miniatura da foto. Revogada quando o item sai da fila. */
  readonly miniatura: string | null;
}

export function DocumentosDaOportunidade({
  oportunidadeId,
}: {
  oportunidadeId: string;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const chave = chaves.documentosDaOportunidade(oportunidadeId);
  const documentos = useQuery({
    queryKey: chave,
    enabled: api !== null,
    refetchInterval: RELER_A_CADA_MS,
    queryFn: ({ signal }) =>
      api!.documentosDaOportunidade({ caminho: { id: oportunidadeId }, sinal: signal }),
  });

  const [fila, definirFila] = useState<NaFila[]>([]);
  const [recusados, definirRecusados] = useState<string[]>([]);
  const [arrastando, definirArrastando] = useState(false);
  const [apagando, definirApagando] = useState<DocumentoDaOportunidade | null>(null);
  const [vendo, definirVendo] = useState<MidiaParaVer | null>(null);
  const seletor = useRef<HTMLInputElement>(null);

  /*
   * As miniaturas são URLs do navegador, e ocupam memória até alguém as
   * revogar. Revogadas ao sair da fila, ao enviar, e ao fechar a aba.
   */
  const filaAtual = useRef(fila);
  filaAtual.current = fila;
  useEffect(
    () => () => {
      for (const item of filaAtual.current) if (item.miniatura) URL.revokeObjectURL(item.miniatura);
    },
    [],
  );

  const acrescentar = useCallback((lista: FileList | null) => {
    const arquivos = Array.from(lista ?? []);
    const grandes = arquivos.filter((a) => a.size > LIMITE_EM_BYTES).map((a) => a.name);
    const cabem = arquivos.filter((a) => a.size <= LIMITE_EM_BYTES);
    definirRecusados(grandes);
    definirFila((atual) => [
      ...atual,
      ...cabem.map((arquivo) => ({
        chave: `${arquivo.name}-${arquivo.size}-${arquivo.lastModified}-${Math.random()}`,
        arquivo,
        miniatura: tipoDoArquivo(arquivo) === 'foto' ? URL.createObjectURL(arquivo) : null,
      })),
    ]);
    // O mesmo arquivo escolhido de novo precisa disparar `change`.
    if (seletor.current) seletor.current.value = '';
  }, []);

  const tirar = (item: NaFila) => {
    if (item.miniatura) URL.revokeObjectURL(item.miniatura);
    definirFila((atual) => atual.filter((i) => i.chave !== item.chave));
  };

  const limpar = () => {
    for (const item of fila) if (item.miniatura) URL.revokeObjectURL(item.miniatura);
    definirFila([]);
    definirRecusados([]);
  };

  const enviar = useMutation({
    mutationFn: () => {
      const formulario = new FormData();
      for (const { arquivo } of fila) formulario.append('arquivos', arquivo, arquivo.name);
      return api!.enviarDocumentosDaOportunidade({
        caminho: { id: oportunidadeId },
        corpo: formulario,
      });
    },
    onSuccess: async () => {
      limpar();
      await cache.invalidateQueries({ queryKey: chave });
    },
  });

  const apagar = useMutation({
    mutationFn: (documento: string) =>
      api!.removerDocumentoDaOportunidade({ caminho: { id: oportunidadeId, documento } }),
    onSuccess: async () => {
      definirApagando(null);
      await cache.invalidateQueries({ queryKey: chave });
    },
  });

  const abrir = (doc: DocumentoDaOportunidade) => {
    const tipo = tipoDoDocumento(doc);
    if (tipo === 'foto' || tipo === 'video') {
      definirVendo({ tipo: tipo === 'foto' ? 'imagem' : 'video', url: doc.url, nome: doc.nome });
    } else {
      window.open(doc.url, '_blank', 'noopener,noreferrer');
    }
  };
  const fecharVisualizador = useCallback(() => definirVendo(null), []);

  const aoArrastar = (evento: DragEvent<HTMLLabelElement>, dentro: boolean) => {
    evento.preventDefault();
    evento.stopPropagation();
    definirArrastando(dentro);
  };

  const itens = documentos.data?.itens ?? [];

  return (
    <div className="documentos-op">
      {/* ── Envio ─────────────────────────────────────────────────────── */}
      <label
        className={arrastando ? 'soltar soltar--arrastando' : 'soltar'}
        onDragEnter={(e) => aoArrastar(e, true)}
        onDragOver={(e) => aoArrastar(e, true)}
        onDragLeave={(e) => aoArrastar(e, false)}
        onDrop={(e) => {
          aoArrastar(e, false);
          acrescentar(e.dataTransfer.files);
        }}
      >
        <span className="soltar__icone">
          <Icone nome="enviar" />
        </span>
        <span className="soltar__texto">
          <span>
            Arraste arquivos aqui ou <span className="soltar__escolher">escolha no computador</span>
          </span>
          <small>
            PDF, fotos, vídeos ou outros, até {LIMITES_DO_DOCUMENTO.megabytesPorArquivo} MB cada
          </small>
        </span>
        <input
          ref={seletor}
          className="soltar__campo"
          type="file"
          multiple
          aria-label="Anexar arquivos"
          onChange={(e) => acrescentar(e.currentTarget.files)}
        />
      </label>

      {recusados.length > 0 && (
        <Faixa tom="atencao">
          {recusados.length === 1
            ? `"${recusados[0]}" passa de ${LIMITES_DO_DOCUMENTO.megabytesPorArquivo} MB e ficou de fora.`
            : `Ficaram de fora por passar de ${LIMITES_DO_DOCUMENTO.megabytesPorArquivo} MB: ${recusados.join(', ')}.`}
        </Faixa>
      )}

      {/* ── A fila, só quando há o que enviar ─────────────────────────── */}
      {fila.length > 0 && (
        <div className="fila-docs">
          <ul className="fila-docs__itens" aria-label="Arquivos para enviar">
            {fila.map((item) => (
              <li key={item.chave} className="fila-docs__item">
                <span className="fila-docs__mini" aria-hidden>
                  {item.miniatura ? (
                    <img src={item.miniatura} alt="" />
                  ) : (
                    <Icone nome={tipoDoArquivo(item.arquivo) === 'pdf' ? 'documento' : 'arquivo'} />
                  )}
                </span>
                <span className="fila-docs__nome" title={item.arquivo.name}>
                  {item.arquivo.name}
                </span>
                <button
                  type="button"
                  className="fila-docs__tirar"
                  aria-label={`Tirar ${item.arquivo.name} da fila`}
                  onClick={() => tirar(item)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          {enviar.isError && (
            <Faixa tom="bloqueado">
              {recadoDeErro(enviar.error, {
                generica: 'Não foi possível enviar os arquivos agora.',
              })}
            </Faixa>
          )}
          <span className="admin__form-acoes">
            <Botao
              variante="fantasma"
              onClick={limpar}
              desabilitado={enviar.isPending}
              motivo="Enviando…"
            >
              Cancelar
            </Botao>
            <Botao
              variante="primario"
              onClick={() => enviar.mutate()}
              desabilitado={
                enviar.isPending ||
                api === null ||
                fila.length > LIMITES_DO_DOCUMENTO.arquivosPorEnvio
              }
              motivo={
                fila.length > LIMITES_DO_DOCUMENTO.arquivosPorEnvio
                  ? `Envie até ${LIMITES_DO_DOCUMENTO.arquivosPorEnvio} arquivos de cada vez.`
                  : 'Enviando…'
              }
            >
              {enviar.isPending ? 'Enviando…' : `Enviar ${arquivosNoPlural(fila.length)}`}
            </Botao>
          </span>
        </div>
      )}

      {/* ── Os anexados ──────────────────────────────────────────────── */}
      <div className="docs__cabecalho">
        <h3>Arquivos anexados</h3>
        {documentos.data && <span>{arquivosNoPlural(itens.length)}</span>}
      </div>

      {documentos.isPending && <p>Buscando os documentos…</p>}
      {documentos.isError && (
        <Faixa tom="bloqueado">
          {recadoDeErro(documentos.error, {
            generica: 'Não foi possível buscar os documentos agora.',
          })}
        </Faixa>
      )}
      {documentos.data && itens.length === 0 && (
        <p className="docs__vazio">
          Nenhum arquivo anexado ainda. Envie o primeiro pela área acima.
        </p>
      )}

      {itens.length > 0 && (
        <ul className="docs__grade" aria-label="Documentos da oportunidade">
          {itens.map((doc) => {
            const tipo = tipoDoDocumento(doc);
            return (
              <li key={doc.referencia_externa} className="doc">
                <div className="doc__previa">
                  <button
                    type="button"
                    className="doc__ver"
                    tabIndex={-1}
                    aria-hidden
                    onClick={() => abrir(doc)}
                  >
                    {tipo === 'foto' ? (
                      <img src={doc.url} alt="" loading="lazy" />
                    ) : tipo === 'video' ? (
                      <video src={doc.url} muted preload="metadata" />
                    ) : (
                      <span className={`doc__icone doc__icone--${tipo}`}>
                        <Icone nome={tipo === 'pdf' ? 'documento' : 'arquivo'} />
                        {tipo === 'pdf' && <span>PDF</span>}
                      </span>
                    )}
                  </button>
                  <span className="doc__acoes">
                    <button
                      type="button"
                      className="doc__acao"
                      aria-label={`Abrir ${doc.nome}`}
                      onClick={() => abrir(doc)}
                    >
                      <Icone nome="olho" />
                    </button>
                    <button
                      type="button"
                      className="doc__acao doc__acao--apagar"
                      aria-label={`Apagar ${doc.nome}`}
                      onClick={() => definirApagando(doc)}
                    >
                      <Icone nome="lixeira" />
                    </button>
                  </span>
                </div>
                <strong className="doc__nome" title={doc.nome}>
                  {doc.nome}
                </strong>
                <span className="doc__meta">
                  <span className={`doc__tipo doc__tipo--${tipo}`}>{ETIQUETA[tipo]}</span>
                  <time dateTime={doc.enviado_em}>{quando(doc.enviado_em)}</time>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {apagando && (
        <Dialogo
          titulo={`Apagar ${apagando.nome}?`}
          largura="estreita"
          consequencia
          aoFechar={() => definirApagando(null)}
          botoes={
            <>
              <Botao variante="fantasma" onClick={() => definirApagando(null)}>
                Cancelar
              </Botao>
              <Botao
                variante="perigo"
                desabilitado={apagar.isPending}
                motivo="Apagando…"
                onClick={() => apagar.mutate(apagando.referencia_externa)}
              >
                {apagar.isPending ? 'Apagando…' : 'Apagar'}
              </Botao>
            </>
          }
        >
          <p>O arquivo sai da oportunidade e do CRM. Não há lixeira para desfazer.</p>
          {apagar.isError && (
            <Faixa tom="bloqueado">
              {recadoDeErro(apagar.error, {
                generica: 'Não foi possível apagar o documento agora.',
              })}
            </Faixa>
          )}
        </Dialogo>
      )}

      {vendo && <VisualizadorDeMidia midia={vendo} aoFechar={fecharVisualizador} />}
    </div>
  );
}
